import { jsxs, jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { ApolloClient, InMemoryCache, HttpLink } from "@apollo/client";
import { Tabs } from "@zillow/constellation-10";
import { SearchBox, buildURL } from "@zillow/search-box";
import { event } from "@zillow/universal-analytics";
import React, { useState, useRef, useMemo } from "react";
import triggerTabClickEvent from "../../utils/clickstream/tab-click.esm.js";
const ALL_CATEGORY_OPTIONS = [{
  value: "buy",
  label: "Buy"
}, {
  value: "rent",
  label: "Rent"
}, {
  value: "sell",
  label: "Sell"
}, {
  value: "zestimate",
  label: "Zestimate"
}];
const tabStyles = css({
  '&[aria-selected="false"]:before': {
    content: "unset !important"
  }
});
function isSuggestionWithZpid(selection) {
  return typeof selection === "object" && selection !== null && "data" in selection && typeof selection.data === "object" && selection.data !== null && "zpid" in selection.data && typeof selection.data.zpid === "number";
}
function getAction(category, origin, searchSelection, location) {
  switch (category) {
    case "buy": {
      const buyPath = buildURL(searchSelection, location, "for_sale");
      return `${origin}${buyPath}`;
    }
    case "rent": {
      const rentPath = buildURL(searchSelection, location, "for_rent");
      return `${origin}${rentPath}`;
    }
    case "sell": {
      if (!isSuggestionWithZpid(searchSelection)) {
        throw new Error(`Could not find zpid for sell category`);
      }
      return `${origin}/sell/options/address-verification/?zpid=${searchSelection.data.zpid}&source=${window.location.origin}${window.location.pathname}`;
    }
    case "zestimate": {
      if (!isSuggestionWithZpid(searchSelection)) {
        throw new Error(`Could not find zpid for zestimate category`);
      }
      return `${origin}/myzillow/yourhome/${searchSelection.data.zpid}`;
    }
    default:
      throw new Error(`Unknown category of "${category}"`);
  }
}
function createSearchBoxApolloClient(uri = "https://zg-graph-dev.int.zgcp-federated-nonprod-k8s.zg-int.net/graphql/") {
  return new ApolloClient({
    ssrMode: typeof window === "undefined",
    link: new HttpLink({
      uri,
      credentials: "same-origin",
      headers: {
        "x-caller-id": "zrs-cms"
      }
    }),
    cache: new InMemoryCache()
  });
}
function isCategory(value) {
  return ["buy", "rent", "sell", "zestimate"].includes(value);
}
const PropertySearch = ({
  propertySearchConfigValues: {
    origin,
    graphURL
  },
  placeholder = "Enter an address, neighborhood, city, or ZIP code",
  categories = ["buy", "rent", "sell", "zestimate"],
  size = "md",
  id,
  t = "",
  opensInNewWindow = true
}) => {
  const [category, setCategory] = useState(categories[0]);
  const inputRef = useRef(null);
  const zgGraphClient = useMemo(() => createSearchBoxApolloClient(graphURL), [graphURL]);
  const handleSubmit = (value, location) => {
    const action = getAction(category, origin, value, location);
    const url = new URL(action);
    if (t) {
      url.searchParams.append("t", t);
    }
    window.open(url.href, opensInNewWindow ? "_blank" : "_self");
    event({
      // https://dataportal.zillowgroup.net/event-registry/2055
      envelope: {
        event_type_id: "2055",
        event_type_version_id: "1",
        event_template_id: "126",
        event_template_version_id: "1"
        // Master ID: 2055.1.126.1
      },
      clickstream_trigger: {
        trigger_type_nm: "interaction",
        trigger_location_nm: "cms",
        trigger_source_nm: "cms_button",
        trigger_object_nm: "no_trigger_object"
      },
      semantic: {
        semantic_event_nm: "cms_submit"
      },
      cms_info: {
        block_id: id || "",
        block_type_cd: "zg_property_search",
        block_target_url: action,
        block_displayed_txt: placeholder,
        form_business_line_txt: category,
        form_submit_label: "magnifying glass icon"
      }
    });
  };
  const boxProps = {
    clientId: "zrs-cms",
    abTests: {
      SXP_SEARCH_BOX_URL_ADDRESS: "BDP_HDP_SRP"
    },
    inputRef,
    handleSubmit,
    recentSearchLocalStorageKey: "recentSearchesEnhanced",
    fetchSavedHomes: true,
    fetchSavedSearches: true,
    platform: "CCMS",
    filterSuggestions: ["sell", "zestimate"].includes(category) ? (suggestions) => suggestions.filter((suggestion) => suggestion.category === "ADDRESS") : void 0,
    platformConfig: {
      CCMS: {
        placeholderText: placeholder,
        supportsLocationIcon: false
      }
    },
    size,
    // Only suggest addresses if sell or zestimate is selected (don't suggest regions)
    resultTypes: ["sell", "zestimate"].includes(category) ? ["ADDRESSES"] : ["REGIONS", "FORSALE", "RENTALS", "SOLD"],
    zgGraphClient
  };
  const categoryOptions = ALL_CATEGORY_OPTIONS.filter((categoryOption) => categories.includes(categoryOption.value));
  const handleTabClick = (value) => {
    var _a;
    setCategory(value);
    const categoryOption = categoryOptions.find((option) => option.value === value);
    if (!categoryOption) {
      throw new Error(`Could not find category option for value of "${value}"`);
    }
    triggerTabClickEvent({
      label: categoryOption.label,
      id: `${id}-${value}`
    });
    const nativeInputValueSetter = (_a = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")) == null ? void 0 : _a.set;
    if (!inputRef.current || !nativeInputValueSetter) {
      return;
    }
    nativeInputValueSetter.call(inputRef.current, "");
    const inputEvent = new Event("input", {
      bubbles: true
    });
    inputRef.current.dispatchEvent(inputEvent);
  };
  const handleSelectedChange = (value) => {
    if (isCategory(value)) {
      handleTabClick(value);
    }
  };
  return /* @__PURE__ */ jsxs(React.Fragment, { children: [
    categoryOptions.length > 1 && /* @__PURE__ */ jsx(Tabs.Root, { defaultSelected: categoryOptions[0].value, onSelectedChange: handleSelectedChange, children: /* @__PURE__ */ jsx(Tabs.List, { id, "aria-label": "Property Search Categories", css: {
      borderBottomColor: "transparent"
    }, children: categoryOptions.map(({
      value,
      label
    }) => /* @__PURE__ */ jsx(Tabs.Tab, { value, type: "button", css: {
      backgroundColor: "transparent !important",
      color: "inherit !important"
    }, className: tabStyles, children: label }, value)) }) }),
    /* @__PURE__ */ jsx(SearchBox, { ...boxProps })
  ] });
};
export {
  PropertySearch as default
};
